// Educational content for the 10 topic sections.
// Written in a friendly, simple tone for 6-8 year olds.

export const TOPICS = [
  {
    id: "introduction",
    number: "01",
    title: "Meet the Tiger!",
    subtitle: "Introduction to tigers",
    emoji: "🐯",
    color: "tiger",
    intro:
      "A tiger is the BIGGEST cat in the whole wide world! It has bright orange fur with black stripes, just like a striped pyjama. Tigers are super strong, super fast, and they love to swim — yes, swim!",
    bullets: [
      "Tigers are the biggest cats on Earth — even bigger than lions.",
      "Every tiger has a unique stripe pattern — like a fingerprint!",
      "A tiger's ROAR can be heard from 3 km away!",
      "They are most active at night and early morning.",
    ],
    funFact:
      "A tiger's stripes are not just on its fur — they're also on its skin! 🤯",
  },
  {
    id: "habitat",
    number: "02",
    title: "Where Tigers Live",
    subtitle: "Habitat",
    emoji: "🌴",
    color: "jungle",
    intro:
      "Tigers live in many different places across Asia. Some live in steamy jungles, some in cold snowy forests, and some near big rivers. They love places with lots of trees, tall grass, and water.",
    bullets: [
      "Tropical rainforests — hot and rainy, full of trees.",
      "Mangrove swamps — like the Sundarbans in India!",
      "Snowy forests — Siberian tigers live here.",
      "Grasslands — tall grass to hide and hunt.",
    ],
    funFact:
      "Most wild tigers live in India — over 70 out of every 100 tigers!",
  },
  {
    id: "food",
    number: "03",
    title: "What Tigers Eat",
    subtitle: "Food",
    emoji: "🍖",
    color: "tiger",
    intro:
      "Tigers are CARNIVORES — that means they eat only meat. They are super hunters who sneak up quietly and pounce on their prey. A tiger can eat up to 40 kg of meat in one meal — that's like 80 burgers!",
    bullets: [
      "Deer, wild boar, water buffalo and antelope are favourites.",
      "Tigers hunt mostly alone, mostly at night.",
      "They drink lots of water and love splashing in streams.",
      "A tiger may eat only once every few days — but a lot!",
    ],
    funFact: "A tiger's tongue is so rough, it can lick paint off the wall!",
  },
  {
    id: "body",
    number: "04",
    title: "Body of a Tiger",
    subtitle: "Built to be the king of cats",
    emoji: "💪",
    color: "tiger",
    intro:
      "Tigers have AMAZING bodies! Powerful legs to run and jump, sharp claws like little knives, and big teeth to bite. Their tail helps them balance, and their whiskers help them feel things in the dark.",
    parts: [
      { name: "Eyes", text: "See 6× better than humans at night." },
      { name: "Ears", text: "Twist around to hear tiny sounds." },
      { name: "Whiskers", text: "Feel the way through tall grass." },
      { name: "Teeth", text: "Long sharp fangs — up to 7 cm long!" },
      { name: "Claws", text: "Pull in and out, super sharp for hunting." },
      { name: "Tail", text: "Almost 1 metre long, helps with balance." },
    ],
    funFact: "A tiger can leap 10 metres in a single jump — that's like 5 cars!",
  },
  {
    id: "types",
    number: "05",
    title: "Types of Tigers",
    subtitle: "Six special cousins",
    emoji: "🌍",
    color: "jungle",
    intro:
      "There are 6 types (called subspecies) of tigers alive today. Each one is a little bit different — some are bigger, some are smaller, some live where it snows!",
    types: [
      { name: "Bengal Tiger", where: "India, Nepal, Bangladesh", icon: "🇮🇳" },
      { name: "Siberian Tiger", where: "Russia (snowy forests)", icon: "❄️" },
      { name: "Indochinese Tiger", where: "Thailand, Vietnam", icon: "🌿" },
      { name: "Malayan Tiger", where: "Malaysia", icon: "🌴" },
      { name: "South China Tiger", where: "Southern China", icon: "🐉" },
      { name: "Sumatran Tiger", where: "Sumatra Island", icon: "🏝️" },
    ],
    funFact:
      "Siberian tigers are the BIGGEST — they can weigh as much as a small car!",
  },
  {
    id: "life",
    number: "06",
    title: "Life of a Tiger",
    subtitle: "From cub to king",
    emoji: "🐾",
    color: "tiger",
    intro:
      "Mommy tigers (called tigresses) have 2 to 4 cute little cubs at a time. The cubs are born tiny and blind, and they drink mommy's milk for many months. Mommy teaches them to hunt and play.",
    stages: [
      { age: "0–2 months", text: "Tiny blind cubs, drink mom's milk." },
      { age: "3–6 months", text: "Open eyes, eat meat, play-fight." },
      { age: "1–2 years", text: "Learn to hunt with mom." },
      { age: "2–3 years", text: "Leave mom, find their own home." },
      { age: "3+ years", text: "Adult! Have their own cubs." },
    ],
    funFact: "Wild tigers live for about 10–15 years.",
  },
  {
    id: "vs-lion",
    number: "07",
    title: "Tiger vs Lion",
    subtitle: "Battle of the big cats!",
    emoji: "🥊",
    color: "tiger",
    intro:
      "Both tigers and lions are amazing — but they are different! Tigers live alone in jungles, while lions live in groups (called prides) on grasslands.",
    compare: [
      { trait: "Where they live", tiger: "Asian jungles", lion: "African savannas" },
      { trait: "Lives in", tiger: "Alone", lion: "Family pride" },
      { trait: "Stripes?", tiger: "Yes! 🐯", lion: "No 🦁" },
      { trait: "Likes water?", tiger: "LOVES it 🌊", lion: "Avoids it" },
      { trait: "Size", tiger: "Bigger 💪", lion: "Smaller" },
      { trait: "Roar heard from", tiger: "3 km away", lion: "8 km away" },
    ],
    funFact: "A tiger is usually heavier and stronger, but a lion has a mane to protect its neck!",
  },
  {
    id: "endangered",
    number: "08",
    title: "Why Tigers are in Trouble",
    subtitle: "Endangered animals",
    emoji: "💔",
    color: "tiger",
    intro:
      "Sadly, there are very few tigers left in the wild — only around 4,500! Long ago there were over 100,000. Tigers are ENDANGERED, which means they could disappear forever if we don't help them.",
    reasons: [
      { title: "Hunting", text: "Some bad people hunt tigers for their fur and bones." },
      { title: "Lost homes", text: "Forests are cut down to build cities and farms." },
      { title: "Less food", text: "Their prey animals are also disappearing." },
      { title: "Climate change", text: "Hotter weather hurts their habitats." },
    ],
    funFact: "100 years ago, there were 9 types of tigers. Now only 6 are left. 😢",
  },
  {
    id: "help",
    number: "09",
    title: "How Can WE Help?",
    subtitle: "Be a tiger hero!",
    emoji: "🦸",
    color: "jungle",
    intro:
      "Every kid can be a TIGER HERO! Here are easy things you and your family can do to help save these amazing animals.",
    actions: [
      { title: "Tell others", text: "Share what you learn about tigers with friends." },
      { title: "Plant a tree", text: "More trees = more homes for animals." },
      { title: "Save paper", text: "Use both sides — saves forests!" },
      { title: "Visit safely", text: "Visit national parks and zoos that protect tigers." },
      { title: "Don't litter", text: "Keep nature clean for animals." },
      { title: "Adopt a tiger", text: "Many groups let families 'adopt' a wild tiger." },
    ],
    funFact:
      "Project Tiger in India has helped tiger numbers DOUBLE in 12 years! 🎉",
  },
  {
    id: "fun-facts",
    number: "10",
    title: "Wild Tiger Facts",
    subtitle: "Did you know?!",
    emoji: "✨",
    color: "tiger",
    intro:
      "Get ready to be amazed — these tiger facts will blow your mind! Take the quiz below after you read them all.",
    facts: [
      "A tiger's pee smells like buttered popcorn! 🍿",
      "Tigers can swim up to 6 km without stopping.",
      "A baby tiger is called a cub.",
      "Tigers cannot purr — but they 'chuff' to say hello.",
      "A group of tigers is called a 'streak' or 'ambush'.",
      "Tigers see in colour, but not as bright as we do.",
      "The tiger is the national animal of India, Bangladesh, Malaysia & South Korea.",
      "White tigers are NOT a separate species — just a rare colour.",
    ],
    funFact: "Tigers have been on Earth for over 2 million years!",
  },
];
